import {
  createElement,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  forwardRef,
  useEffect,
  FocusEvent,
} from 'react';
import { useDesignTokens, renderInlineCssVars, DesignTokens } from '../../theme';
import { TextAreaProps, StatusType } from './';
import { Icon } from '../Icon';
import { InvisibleText } from '../InvisibleText';
import useOnFocusOut from '../../hooks/useOnFocusOut';
import { ReactKeyboardEvent, ReactChangeEvent } from '../../react-synthetic-events';
import { MessagePopover } from '../Input/__internal__/MessagePopover';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { postfixTestId } from '../../util/testId';
import { setRef } from '../../util/ref-util';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useEarcon, EarconName } from '../../hooks/useEarcon';
import bundle from './NovaComponents-TextArea-gui-icu.properties';

import cx from 'classnames';
import cn from './TextArea.css.json';
import './TextArea.css';

const useHtmlId = createUseHtmlId('TextArea');

const useTextLocalizer = createUseTextLocalizer(bundle);

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  props,
  ref
) {
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [hasHover, setHasHover] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [shouldSelect, setShouldSelect] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const earcon = useEarcon({ source: EarconName.InputFull });

  const { disabled, readOnly, required, value, autoExpand } = props;

  // The reason storing the required TA's status is that the TA will rerender when hovering or focus the TA, and the previous status should be active.
  const status = props.status ?? (showRequiredError ? 'alert' : null);

  {
    // NOTE: this select the text in the textbox when the user changes the focus in the OptionPopper.
    //     | The main issue with this approach is we assume the text in the textbox will change before this happens.
    useLayoutEffect(
      function selectValue() {
        if (disabled || readOnly || !shouldSelect) return;
        setShouldSelect(false);
        setTimeout(() => {
          const textarea = textAreaRef.current;
          if (textarea && !props.disableAutoSelection) {
            textarea.setSelectionRange(0, textarea.value?.length, undefined);
          }
        }, 0);
      },
      [shouldSelect, readOnly, disabled, props.disableAutoSelection]
    );
  }

  let onBlur, onFocus;
  {
    const handlers = useOnFocusOut(() => {
      if (disabled || readOnly) return;
      setHasFocus(false);
      // TODO: don't need to fire if list item was clicked, we currently fire twice in that case.
      if (props.onCommit) {
        props.onCommit({ value });
      }
    });
    onBlur = () => {
      if (disabled || readOnly) return;
      handlers.onBlur();
      setHasHover(false);
      setHasFocus(false);
      setIsTyping(false);
      setShowRequiredError(!!required && value?.length < 1);
      if (props.onBlur) props.onBlur();
    };
    onFocus = (e: FocusEvent<HTMLDivElement>) => {
      if (disabled || readOnly) return;
      handlers.onFocus();
      setHasFocus(true);
      setShouldSelect(true);
      if (props.onFocus) props.onFocus(e);
    };
  }

  function handleChange(e: ReactChangeEvent<HTMLTextAreaElement>) {
    if (disabled || readOnly) return;
    const liveValue = e.target.value;
    setShowRequiredError(false);
    setIsTyping(true);
    props.onChange?.({ value: liveValue });
  }

  function handleKeyPress(event: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (disabled || readOnly) return;
    if (event.key === 'Enter') {
      // user is committing the current value
      // TODO: do we need to reset the selected index too?
      if (props.onCommit) {
        props.onCommit({ value });
      }
    }
  }

  const handleMouseEnter = () => setHasHover(true);

  const handleMouseLeave = () => setHasHover(false);

  const iconType =
    status === 'alert' ? ErrorStatus : status === 'warning' ? WarningStatus : undefined;

  const [validationMessageId, hiddenContentId] = useHtmlId(2);

  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';
  const dt = useDesignTokens();
  const textAreaTokens = {
    ...renderInlineCssVars(
      {
        ...getTextAreaTokens(dt, props, status),
        pd: dt.spacing.insetSquish.s.padding,
        'mg-xs': dt.spacing.core.xs,
        'font-size-m': dt.fontNova.size.m,
        'editable-focus-outline':
          dt.themeVariant === 'high-contrast' || isCustomFocusTurnedOn
            ? 'var(--focus-light-bg)'
            : 'none',
      },
      'textarea'
    ),
    ...renderInlineCssVars(
      { fg: dt.c.colorTextLabel, 'disabled-fg': dt.stateStandardNova.disabled.fg },
      'textarea-placeholder'
    ),
    ...renderInlineCssVars(
      {
        ...(status === 'alert' || status === 'warning'
          ? dt.stateStatusNova[status]
          : dt.stateInputNova.focused),
      },
      'textarea-focus'
    ),
  };

  const localize = useTextLocalizer();

  const ariaDescribedBy = [];
  const describedByEnable = disabled || readOnly || (hasFocus && !isTyping);
  if (describedByEnable) ariaDescribedBy.push(hiddenContentId);
  else if (status) ariaDescribedBy.push(validationMessageId);
  if (props.ariaDescribedBy) ariaDescribedBy.push(...props.ariaDescribedBy);

  useEffect(() => {
    updateHeight(autoExpand, textAreaRef.current);
  }, [autoExpand, value, localize]);

  const [setTextAreaResizeRef] = useResizeObserverRef<HTMLTextAreaElement>(() => {
    updateHeight(autoExpand, textAreaRef.current);
  });

  const setTextAreaRef = useCallback(
    (textArea: HTMLTextAreaElement | null) => {
      textAreaRef.current = textArea;
      setRef(ref, textArea);
      setTextAreaResizeRef(textArea);
    },
    [ref, setTextAreaResizeRef]
  );

  const playEarcon = (textLength: number) => {
    if (textAreaRef.current) {
      const start = textAreaRef.current.selectionStart;
      const end = textAreaRef.current.selectionEnd;
      if (props.maxLength && start + textLength - end >= props.maxLength) earcon.play();
    }
  };

  useEffect(() => {
    const element = textAreaRef.current;
    if (!autoExpand || element === null) return;
    if (element.parentElement && getComputedStyle(element.parentElement).maxHeight !== 'none') {
      // consumer set max-height style on the container
      element.style.maxHeight = 'inherit';
      return;
    }
    if (props.maxRows === undefined || props.maxRows < 1) return;
    const elementStyles = getComputedStyle(element);
    element.style.maxHeight = `${
      parseFloat(elementStyles.lineHeight) * props.maxRows +
      parseFloat(elementStyles.paddingTop) +
      parseFloat(elementStyles.paddingBottom) +
      parseFloat(elementStyles.borderTopWidth) +
      parseFloat(elementStyles.borderBottomWidth)
    }px`;
  }, [autoExpand, props.maxRows, props.style]);

  const message = required
    ? props.message ?? localize('required.invalidInput.label')
    : props.message ?? '';

  return (
    <div
      data-testid={props.testId}
      className={cx(cn.container, props.height ? cn.fixedHeight : '', props.className)}
      onBlur={onBlur}
      onFocus={onFocus}
      style={{ height: props.height, width: props.width, ...textAreaTokens, ...props.style }}
    >
      {describedByEnable ? <InvisibleText id={hiddenContentId}>{value}</InvisibleText> : null}
      <textarea
        data-testid={postfixTestId(props.testId, 'textarea')}
        tabIndex={props.tabIndex ?? undefined}
        ref={setTextAreaRef}
        className={cx(
          cn.textarea,
          !readOnly && !disabled && cn.editable,
          readOnly && cn.readOnly,
          disabled && cn.disabled,
          !hasFocus && status && cn.statusFocusout
        )}
        style={{ lineHeight: props.style?.lineHeight }}
        readOnly={!!readOnly || !!disabled}
        aria-required={!!required}
        rows={props.rows ? props.rows : undefined}
        placeholder={props.placeholder ? props.placeholder : undefined}
        maxLength={props.maxLength ? props.maxLength : undefined}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onKeyDown={(e) => {
          const isCtrlOrMetaKey = e.ctrlKey || e.metaKey;
          if (!readOnly && e.key === 'Enter') {
            e.stopPropagation();
          }
          if (
            textAreaRef.current &&
            !disabled &&
            !readOnly &&
            !isCtrlOrMetaKey &&
            // ignore the unprintable characters, and the IME consideration
            (e.key.length === 1 || e.keyCode === 229)
          ) {
            playEarcon(value?.length);
          }
          props.onKeyDown && props.onKeyDown(e);
        }}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text');
          const length = value?.length + text.length;
          playEarcon(length > 0 ? length - 1 : length);
        }}
        onMouseUp={(e) => {
          props.onMouseUp?.(e);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        autoComplete="off"
        value={value}
        aria-disabled={!!disabled}
        aria-label={props.ariaLabel}
        aria-labelledby={props.ariaLabelledBy}
        aria-describedby={ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined}
        id={props.id}
      />
      {status ? (
        <span aria-live="polite" role="status" className={cn.statusMessageContainer}>
          <span className={cn.offscreen} id={validationMessageId}>
            {localize('invalidInput.label', { message })}
          </span>
        </span>
      ) : null}
      {!hasFocus && status && iconType ? (
        <div
          className={cn.statusIcon}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Icon icon={iconType} color={status} />
        </div>
      ) : null}
      {!status || !message ? null : (
        <MessagePopover
          status={status}
          referenceElementRef={textAreaRef}
          message={message}
          hidden={!hasFocus && !hasHover}
          testId={postfixTestId(props.testId, 'messagePopper')}
        />
      )}
    </div>
  );
});

function getTextAreaTokens(dt: DesignTokens, props: TextAreaProps, status: StatusType) {
  if (props.disabled) {
    return {
      ...dt.stateInputNova.disabled,
      'disabled-focus-bd': dt.themeVariant !== 'high-contrast',
    };
  } else if (props.readOnly) {
    return dt.stateInputNova.readOnly;
  } else {
    switch (status) {
      case 'alert':
        return {
          ...dt.stateStatusNova.alert,
        };
      case 'warning':
        return {
          ...dt.stateStatusNova.warning,
        };
      default:
        return {
          ...dt.stateInputNova.normal,
        };
    }
  }
}

const updateHeight = (autoExpand: boolean | undefined, element: HTMLTextAreaElement | null) => {
  if (autoExpand && element) {
    // scrollbar will occupy some space, try best to not scroll
    element.style.overflowY = 'hidden';
    // When value changed shorter, we need to set auto height first to let it shrink
    element.style.height = 'auto';
    const scrollHeight = element.scrollHeight;
    const borderWidth = parseFloat(getComputedStyle(element).borderWidth) || 0;
    // when zooming in the window, the value of offsetWidth - clientWidth may be 1px not 2px,
    // this may cause a scrollbar show in the textarea, and at this time the height of the textarea did not reach the max-height.
    element.style.height = `${Math.ceil(scrollHeight + 2 * borderWidth)}px`;
    // recover after calculation
    element.style.overflowY = 'auto';
  }
};
